import React, { useEffect, useState } from 'react';
import { getDayTranslationKey, saveSelectedDate, getInitialDayOffset } from '../utils/dateUtils';
import { useI18n } from '../i18n/I18nContext';
import { useMapContext } from '../contexts/MapContext';
import { usePointWindgram } from '../hooks/usePointWindgram';
import { Overlay } from './Overlay';
import { WindgramView } from './WindgramView';
import { ExternalLinks } from './ExternalLinks';

/**
 * Il windgram di un punto qualunque, aperto con la pressione lunga sulla mappa.
 * Ritaglio e cella li sceglie il server; la cella torna nella risposta e si disegna sulla
 * mappa (MapPage), cosi' si vede a quale quadrato di griglia si riferisce il grafico.
 */
export const PointOverlay: React.FC<{ lat: number; lon: number }> = ({ lat, lon }) => {
  const { t } = useI18n();
  const { setSelectedCell } = useMapContext();
  const [selectedDay, setSelectedDay] = useState(() => getInitialDayOffset());
  const [reload, setReload] = useState(0);
  const res = usePointWindgram(lat, lon, selectedDay, reload);

  // la cella e' la stessa tutti i giorni (stesso ritaglio), ma arriva con la risposta
  useEffect(() => {
    if (res.state === 'ok') setSelectedCell({ point: [lat, lon], cell: res.data.cella });
  }, [res, lat, lon, setSelectedCell]);

  const title = t('wg.point', { lat: lat.toFixed(3), lon: lon.toFixed(3) });

  return (
    <Overlay title={title} className="spot-overlay" zIndex={2100} alignItems="center">
      <>
        <div className="day-selector">
          {[0, 1, 2, 3, 4].map((day) => (
            <button
              key={day}
              className={`day-button ${selectedDay === day ? 'day-button-active' : ''}`}
              onClick={() => { setSelectedDay(day); saveSelectedDate(day); }}
            >
              {t(getDayTranslationKey(day))}
            </button>
          ))}
        </div>

        <div className="forecast-content-container">
          <div className="spot-overlay-content">
            {res.state === 'loading' && (
              <div className="windgram-loading"><p>{t('wg.loading')}</p></div>
            )}
            {res.state === 'na' && (
              <div className="windgram-error">
                <p>{t('wg.notAvailable')}</p>
                <p className="wg-hint">{t('wg.notAvailableHint')}</p>
              </div>
            )}
            {res.state === 'credit' && (
              <div className="windgram-error"><p>{t('wg.noCredit')}</p></div>
            )}
            {res.state === 'error' && (
              <div className="windgram-error">
                <p>{t('wg.loadError')}</p>
                <button onClick={() => setReload((n) => n + 1)} className="retry-button">{t('spot.retry')}</button>
              </div>
            )}
            {res.state === 'ok' && <WindgramView data={res.data} />}
          </div>
          <ExternalLinks lat={lat} lng={lon} />
        </div>
      </>
    </Overlay>
  );
};
